/**
 * Suggested daily activities while wearing the Back Pain Transformer™.
 *
 * Users pick ONE activity from this catalog, perform it, and mark it complete.
 * Each completion counts toward the "7 activities this week" target.
 * Repeats are allowed and encouraged.
 *
 * Adding/removing an item is a 1-line change. Each activity needs a stable
 * `id` (used for analytics + admin reporting) so DO NOT rename `id` once
 * shipped — only the `label` is safe to tweak.
 */

import {
  Home, Bed, Shirt, UtensilsCrossed, Soup, ChefHat, Sparkles, Brush,
  Trash2, Coffee, BookOpen, Leaf,
  Footprints, MapPin, Sun, ShoppingBag, ShoppingCart, Dog, Briefcase,
  PhoneCall, Plane,
  Monitor, Laptop, Video, FileText, PenLine, Lightbulb, Presentation, Users,
  Folder, LayoutDashboard,
  Palette, Pencil, Camera, Hammer, Flower2, Sprout, Scissors, Music, Guitar,
  Store, Building2, PackageOpen, Pill, Mail, Glasses, Library, Luggage,
  Smile, Heart, Trophy, Tent, Landmark, PartyPopper, Gamepad2,
  TreePine, Fish, Bird, Salad,
  Wind, Brain, Activity, Move, Dumbbell, MoveDiagonal,
  StretchHorizontal,
} from "lucide-react";

export const ACTIVITY_CATEGORIES = [
  {
    id: "home",
    label: "Around the House",
    emoji: "🏡",
    accent: "#22D3EE",
    desc: "Everyday motions of your home life",
    activities: [
      { id: "morning-routine", label: "Morning routine", icon: Sun },
      { id: "making-bed", label: "Making the bed", icon: Bed },
      { id: "folding-laundry", label: "Folding laundry", icon: Shirt },
      { id: "dishwasher", label: "Loading & unloading the dishwasher", icon: UtensilsCrossed },
      { id: "washing-dishes", label: "Washing dishes", icon: Soup },
      { id: "preparing-meals", label: "Preparing meals", icon: ChefHat },
      { id: "cooking-dinner", label: "Cooking dinner", icon: ChefHat },
      { id: "vacuuming", label: "Vacuuming", icon: Sparkles },
      { id: "sweeping", label: "Light sweeping", icon: Brush },
      { id: "dusting", label: "Dusting", icon: Sparkles },
      { id: "watering-plants", label: "Watering houseplants", icon: Leaf },
      { id: "organize-closets", label: "Organizing closets", icon: Home },
      { id: "groceries-away", label: "Putting groceries away", icon: PackageOpen },
      { id: "light-tidy", label: "Light tidying", icon: Home },
      { id: "recycling", label: "Taking out the recycling", icon: Trash2 },
      { id: "coffee-tea", label: "Making coffee or tea", icon: Coffee },
      { id: "read-standing", label: "Reading while standing", icon: BookOpen },
      { id: "gentle-stretch", label: "Gentle stretching", icon: StretchHorizontal },
    ],
  },
  {
    id: "walking",
    label: "Walking & Mobility",
    emoji: "🚶",
    accent: "#10B981",
    desc: "Anywhere your feet take you",
    activities: [
      { id: "walk-neighborhood", label: "Walking around the neighborhood", icon: Footprints },
      { id: "walk-park", label: "Walking in the park", icon: MapPin },
      { id: "walk-beach", label: "Walking on the beach", icon: Sun },
      { id: "walk-mall", label: "Walking around the mall", icon: ShoppingBag },
      { id: "grocery-shop", label: "Grocery shopping", icon: ShoppingCart },
      { id: "farmers-market", label: "Visiting a farmer's market", icon: Salad },
      { id: "walk-dog", label: "Walking the dog", icon: Dog },
      { id: "walk-office", label: "Walking around the office", icon: Briefcase },
      { id: "walk-phone", label: "Walking while on the phone", icon: PhoneCall },
      { id: "sightseeing", label: "Sightseeing while traveling", icon: Plane },
    ],
  },
  {
    id: "office",
    label: "Office & Work",
    emoji: "💻",
    accent: "#38BDF8",
    desc: "Stand more, sit less, stay aligned",
    activities: [
      { id: "standing-desk", label: "Standing desk work", icon: Monitor },
      { id: "light-computer", label: "Light computer work", icon: Laptop },
      { id: "video-meetings", label: "Video meetings", icon: Video },
      { id: "reading-docs", label: "Reading documents", icon: FileText },
      { id: "writing-notes", label: "Writing notes", icon: PenLine },
      { id: "brainstorming", label: "Brainstorming", icon: Lightbulb },
      { id: "teaching", label: "Teaching", icon: Presentation },
      { id: "presentations", label: "Presentations", icon: Users },
      { id: "filing", label: "Filing paperwork", icon: Folder },
      { id: "organize-workspace", label: "Organizing workspaces", icon: LayoutDashboard },
    ],
  },
  {
    id: "creative",
    label: "Creative Activities",
    emoji: "🎨",
    accent: "#F59E0B",
    desc: "Make something with your hands",
    activities: [
      { id: "painting", label: "Painting", icon: Palette },
      { id: "drawing", label: "Drawing", icon: Pencil },
      { id: "photography", label: "Photography", icon: Camera },
      { id: "woodworking", label: "Woodworking (light)", icon: Hammer },
      { id: "gardening-light", label: "Gardening (light)", icon: Flower2 },
      { id: "potting-plants", label: "Potting plants", icon: Sprout },
      { id: "sewing", label: "Sewing", icon: Scissors },
      { id: "crafting", label: "Crafting", icon: Scissors },
      { id: "scrapbooking", label: "Scrapbooking", icon: BookOpen },
      { id: "guitar-standing", label: "Playing guitar (standing)", icon: Guitar },
      { id: "music-practice", label: "Music practice", icon: Music },
    ],
  },
  {
    id: "errands",
    label: "Everyday Errands",
    emoji: "🛍️",
    accent: "#EC4899",
    desc: "Wear it as you go",
    activities: [
      { id: "errand-grocery", label: "Grocery shopping", icon: ShoppingCart },
      { id: "errand-costco", label: "Shopping at Costco", icon: Store },
      { id: "errand-ikea", label: "Walking through IKEA", icon: Building2 },
      { id: "errand-prescription", label: "Picking up prescriptions", icon: Pill },
      { id: "errand-post", label: "Visiting the post office", icon: Mail },
      { id: "errand-window", label: "Window shopping", icon: Glasses },
      { id: "errand-bookstore", label: "Browsing bookstores", icon: Library },
      { id: "errand-airport", label: "Walking airports", icon: Plane },
      { id: "errand-bags", label: "Carrying light shopping bags", icon: Luggage },
    ],
  },
  {
    id: "family",
    label: "Family Activities",
    emoji: "👨‍👩‍👧",
    accent: "#A78BFA",
    desc: "Be present with the people you love",
    activities: [
      { id: "play-grandkids", label: "Playing with grandchildren", icon: Smile },
      { id: "walk-spouse", label: "Walking with your spouse", icon: Heart },
      { id: "family-outing", label: "Family outings", icon: PartyPopper },
      { id: "visit-friends", label: "Visiting friends", icon: Users },
      { id: "sports-event", label: "Watching sporting events", icon: Trophy },
      { id: "the-zoo", label: "Going to the zoo", icon: Tent },
      { id: "museums", label: "Visiting museums", icon: Landmark },
      { id: "community-event", label: "Attending community events", icon: Users },
      { id: "yard-games", label: "Light yard games", icon: Gamepad2 },
    ],
  },
  {
    id: "outdoor",
    label: "Outdoor Activities",
    emoji: "🌳",
    accent: "#65A30D",
    desc: "Be moved by what's outside",
    activities: [
      { id: "nature-walk", label: "Nature walks", icon: TreePine },
      { id: "botanical", label: "Botanical gardens", icon: Flower2 },
      { id: "easy-hike", label: "Easy hiking trails", icon: TreePine },
      { id: "bird-watching", label: "Bird watching", icon: Bird },
      { id: "fishing", label: "Fishing", icon: Fish },
      { id: "photo-walk", label: "Photography walks", icon: Camera },
      { id: "boardwalk", label: "Walking the boardwalk", icon: Footprints },
      { id: "picnics", label: "Picnics", icon: Salad },
      { id: "gentle-garden", label: "Gentle gardening", icon: Sprout },
      { id: "parks", label: "Visiting parks", icon: TreePine },
    ],
  },
  {
    id: "wellness",
    label: "Wellness Activities",
    emoji: "🧘",
    accent: "#22D3EE",
    desc: "Deepen the reset state",
    activities: [
      { id: "breathing", label: "Breathing exercises", icon: Wind },
      { id: "meditation", label: "Meditation", icon: Brain },
      { id: "tai-chi", label: "Tai Chi", icon: Move },
      { id: "mobility", label: "Gentle mobility routines", icon: MoveDiagonal },
      { id: "balance", label: "Balance exercises", icon: Activity },
      { id: "pt-homework", label: "Physical therapy homework", icon: Dumbbell },
      { id: "warm-up", label: "Warm-up exercises", icon: StretchHorizontal },
      { id: "cool-down", label: "Cool-down walks", icon: Footprints },
    ],
  },
];

/** Flat lookup map id → { ...activity, category } */
export const ACTIVITY_INDEX = ACTIVITY_CATEGORIES.reduce((acc, cat) => {
  for (const a of cat.activities) {
    acc[a.id] = { ...a, category: cat.id, categoryLabel: cat.label, accent: cat.accent };
  }
  return acc;
}, {});

export const ACTIVITY_TARGET_PER_WEEK = 7;
